import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  ArrowLeft,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Package,
  TrendingUp
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import employeeMasterService from '../../services/employeeMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const EmployeeMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    inactiveEmployees: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [employeesResponse, statsResponse] = await Promise.all([
        employeeMasterService.getAllEmployees(),
        employeeMasterService.getEmployeeStats()
      ])
      
      if (employeesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (employeesResponse.data || []).map(employee => ({
          ...employee,
          id: employee._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (employeesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No employee master records found. Create your first employee!')
        }
      } else {
        toast.error(employeesResponse.message || 'Failed to load employees')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading employee master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view employees')
      } else {
        toast.error('Failed to load employee master records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'employeeCode',
      header: 'Employee Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee Name',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'department',
      header: 'Department',
      cell: ({ getValue }) => (
        <div className="sac-cell" style={{ textAlign: 'center' }}>
          <span className="sac-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'designation',
      header: 'Designation',
      cell: ({ getValue }) => (
        <div className="sac-cell" style={{ textAlign: 'center' }}>
          <span className="sac-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue()?.toLowerCase() === 'active' ? 'active' : 'inactive'}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/expenses/employee-master/form')
  }

  const handleEdit = (employeeData) => {
    navigate('/expenses/employee-master/edit', { state: { employeeData } })
  }

  const handleView = (employeeData) => {
    navigate('/expenses/employee-master/view', { state: { employeeData } })
  }

  const handleDelete = (employeeData) => {
    setDeleteItem(employeeData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting employee...')
      // Use _id instead of id
      const response = await employeeMasterService.deleteEmployee(deleteItem._id)
      if (response.success) {
        toast.success('Employee deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete employee')
      }
    } catch (error) {
      console.error('Error deleting employee:', error)
      if (error.response?.status === 404) {
        toast.error('Employee not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this employee')
      } else {
        toast.error('Failed to delete employee. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    // TODO: Implement export functionality
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    {
      label: 'Expenses',
      path: '/expenses',
      onClick: () => navigate('/expenses')
    },
    {
      label: 'Employee Master',
      path: '/expenses/employee-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Employee Master</h1>
          <p>Manage employee profiles and expense limits</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            Add Employee
          </button>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.totalEmployees}</span>
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalEmployees}</div>
          <div className="metric-title">Total Employees</div>
          <div className="metric-subtitle">All employees</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.activeEmployees}</span>
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeEmployees}</div>
          <div className="metric-title">Active Employees</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.inactiveEmployees}</span>
            </div>
            <div className="metric-trend negative">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveEmployees}</div>
          <div className="metric-title">Inactive Employees</div>
          <div className="metric-subtitle">Currently inactive</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onExport={handleExport}
          noCard={true}
          className="sac-master-table"
          pageSize={10}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Employee"
        message={`Are you sure you want to delete "${deleteItem?.employeeName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default EmployeeMaster

